import Phaser from 'phaser';
import { ENEMY_TYPES } from '../entities/EnemyTypes';

const ATTRACT_MODE_DURATION = 60_000; // 60 seconds
const BOT_REACTION_DELAY = 300; // ms
const BOT_MISS_RATE = 0.15; // 15%

export class AttractGameScene extends Phaser.Scene {
  private player!: Phaser.Physics.Arcade.Sprite;
  private enemies!: Phaser.Physics.Arcade.Group;
  private projectiles!: Phaser.Physics.Arcade.Group;
  private xpOrbs!: Phaser.Physics.Arcade.Group;

  private killCount: number = 0;
  private survivalTime: number = 0;
  private lastEnemySpawn: number = 0;
  private readonly enemySpawnRate: number = 2000; // base ms between spawns
  private gameStartTime: number = 0;
  private difficultyMultiplier: number = 1;
  private isGameOver: boolean = false;
  private isPaused: boolean = false;

  private killCountText!: Phaser.GameObjects.Text;
  private survivalTimeText!: Phaser.GameObjects.Text;
  private hpText!: Phaser.GameObjects.Text;
  private levelText!: Phaser.GameObjects.Text;
  private xpBarGraphics!: Phaser.GameObjects.Graphics;

  // Bot control state
  private botState: {
    targetX: number;
    targetY: number;
    lastDecisionTime: number;
    decisionInterval: number;
    isMoving: boolean;
  } = {
    targetX: 0,
    targetY: 0,
    lastDecisionTime: 0,
    decisionInterval: 300, // 300ms reaction delay
    isMoving: false,
  };

  // Attract mode timer
  private attractTimerEvent?: Phaser.Time.TimerEvent;

  // Arena boundaries
  private readonly ARENA_WIDTH = 1000;
  private readonly ARENA_HEIGHT = 700;
  private readonly ARENA_OFFSET_X = 100;
  private readonly ARENA_OFFSET_Y = 50;

  constructor() {
    super({ key: 'AttractGameScene' });
  }

  create() {
    const { width, height } = this.cameras.main;
    this.gameStartTime = Date.now();
    this.killCount = 0;
    this.survivalTime = 0;
    this.difficultyMultiplier = 1;
    this.isGameOver = false;
    
    // Initialize bot state
    this.botState.lastDecisionTime = Date.now();
    this.botState.decisionInterval = BOT_REACTION_DELAY;
    this.botState.targetX = width / 2;
    this.botState.targetY = height / 2;

    // Arena background
    this.add.rectangle(
      this.ARENA_OFFSET_X + this.ARENA_WIDTH / 2,
      this.ARENA_OFFSET_Y + this.ARENA_HEIGHT / 2,
      this.ARENA_WIDTH,
      this.ARENA_HEIGHT,
      0x1a1a2e,
    ).setStrokeStyle(2, 0x444466);

    // Procedural textures
    this.buildTextures();

    // Player sprite
    this.player = this.physics.add.sprite(width / 2, height / 2, 'player');
    this.player.setCircle(16);
    this.player.setCollideWorldBounds(false);
    this.player.setDepth(10);

    // Player stats — veteran (intermediate) difficulty baseline
    const p = this.player as unknown as Record<string, number>;
    p['hp'] = 100;
    p['maxHp'] = 100;
    p['speed'] = 200;
    p['damage'] = 25;
    p['fireRate'] = 500;
    p['lastFire'] = 0;
    p['projectileCount'] = 1;
    p['projectileSpeed'] = 500;
    p['pickupRadius'] = 80;
    p['hpRegen'] = 0;
    p['lifesteal'] = 0;
    p['level'] = 1;
    p['xp'] = 0;
    p['xpToNextLevel'] = 100;

    // Physics groups
    this.enemies = this.physics.add.group();
    this.projectiles = this.physics.add.group();
    this.xpOrbs = this.physics.add.group();

    // Collision callbacks
    this.physics.add.overlap(
      this.projectiles,
      this.enemies,
      this.projectileHitEnemy as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    );

    this.physics.add.overlap(
      this.player,
      this.enemies,
      this.playerHitEnemy as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    );

    this.createHUD();
    this.spawnWave(); // seed with one wave immediately

    // Set up attract mode timer (60 seconds max)
    this.attractTimerEvent = this.time.delayedCall(ATTRACT_MODE_DURATION, () => {
      this.endAttractMode();
    });

    // End attract mode on any click/tap
    this.input.on('pointerdown', () => {
      this.endAttractMode();
    });

    // Also listen for game over to end attract mode
    window.gameInstance = this.game;
  }

  private buildTextures() {
    const circle = (key: string, radius: number, color: number) => {
      if (this.textures.exists(key)) return;
      const g = this.add.graphics();
      g.fillStyle(color, 1);
      g.fillCircle(radius, radius, radius);
      g.generateTexture(key, radius * 2, radius * 2);
      g.destroy();
    };

    circle('player', 16, 0x00d9ff);
    circle('enemy_melee', 12, 0xff4444);
    circle('enemy_tank', 20, 0xff8800);
    circle('enemy_swarmer', 7, 0xdd44ff);
    circle('projectile', 4, 0xffff00);
    circle('xp_orb', 5, 0x00ff88);
  }

  private createHUD() {
    const { width } = this.cameras.main;

    this.killCountText = this.add.text(20, 20, 'Kills: 0', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: 'rgba(0,0,0,0.5)',
      padding: { x: 10, y: 5 },
    }).setDepth(100);

    this.survivalTimeText = this.add.text(20, 55, 'Time: 0:00', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: 'rgba(0,0,0,0.5)',
      padding: { x: 10, y: 5 },
    }).setDepth(100);

    this.hpText = this.add.text(20, 90, 'HP: 100/100', {
      fontSize: '20px',
      color: '#00ff00',
      backgroundColor: 'rgba(0,0,0,0.5)',
      padding: { x: 10, y: 5 },
    }).setDepth(100);

    this.levelText = this.add.text(width - 20, 20, 'LVL 1', {
      fontSize: '20px',
      color: '#4488ff',
      backgroundColor: 'rgba(0,0,0,0.5)',
      padding: { x: 10, y: 5 },
    }).setOrigin(1, 0).setDepth(100);

    this.xpBarGraphics = this.add.graphics().setDepth(100);

    // Attract mode indicator
    this.add.text(20, 125, '[ATTRACT MODE]', {
      fontSize: '16px',
      color: '#ff6b35',
      backgroundColor: 'rgba(0,0,0,0.5)',
      padding: { x: 8, y: 4 },
    }).setDepth(100);
  }

  private updateHUD() {
    const { width, height } = this.cameras.main;
    const p = this.player as unknown as Record<string, number>;

    this.killCountText.setText(`Kills: ${this.killCount}`);

    const mins = Math.floor(this.survivalTime / 60);
    const secs = Math.floor(this.survivalTime % 60);
    this.survivalTimeText.setText(`Time: ${mins}:${secs.toString().padStart(2, '0')}`);

    const hp = p['hp'];
    const maxHp = p['maxHp'];
    this.hpText.setText(`HP: ${Math.max(0, Math.floor(hp))}/${maxHp}`);
    this.hpText.setColor(hp > 60 ? '#00ff00' : hp > 30 ? '#ffff00' : '#ff4444');

    this.levelText.setText(`LVL ${p['level']}`);

    // XP bar
    const xpProgress = Math.min(p['xp'] / p['xpToNextLevel'], 1);
    const barW = 400;
    const barH = 18;
    const barX = (width - barW) / 2;
    const barY = height - 34;

    this.xpBarGraphics.clear();
    this.xpBarGraphics.fillStyle(0x222244);
    this.xpBarGraphics.fillRect(barX, barY, barW, barH);
    if (xpProgress > 0) {
      this.xpBarGraphics.fillStyle(0x4488ff);
      this.xpBarGraphics.fillRect(barX, barY, barW * xpProgress, barH);
    }
    this.xpBarGraphics.lineStyle(1, 0x6666aa);
    this.xpBarGraphics.strokeRect(barX, barY, barW, barH);
  }

  update(time: number, delta: number) {
    if (this.isPaused || this.isGameOver) return;

    this.survivalTime = (Date.now() - this.gameStartTime) / 1000;

    // HP regen (frame-rate independent)
    const p = this.player as unknown as Record<string, number>;
    if (p['hpRegen'] > 0) {
      p['hp'] = Math.min(p['hp'] + p['hpRegen'] * delta / 1000, p['maxHp']);
    }

    // Difficulty ramp: spawn rate halves by ~4 minutes; speed scales up too
    this.difficultyMultiplier = 1 + (this.survivalTime / 60) * 0.5;

    // Enemy spawning
    const spawnInterval = Math.max(400, this.enemySpawnRate / this.difficultyMultiplier);
    if (time - this.lastEnemySpawn > spawnInterval) {
      const spawnCount = Math.min(4, 1 + Math.floor(this.survivalTime / 120));
      for (let i = 0; i < spawnCount; i++) {
        this.spawnWave();
      }
      this.lastEnemySpawn = time;
    }

    // Bot-controlled player movement
    this.handleBotMovement(time);
    this.autoFire(time, p);
    this.updateXPOrbs();
    this.updateProjectiles();
    this.updateEnemies();
    this.updateHUD();

    if (p['hp'] <= 0) {
      this.triggerGameOver();
    }
  }

  private handleBotMovement(_time: number) {
    const p = this.player as unknown as Record<string, number>;
    const speed = p['speed'];
    const vel = { x: 0, y: 0 };

    // Bot makes decisions with reaction delay
    const now = Date.now();
    if (now - this.botState.lastDecisionTime >= this.botState.decisionInterval) {
      this.botState.lastDecisionTime = now;
      this.updateBotDecision();
    }

    // Move towards bot's target position
    const dx = this.botState.targetX - this.player.x;
    const dy = this.botState.targetY - this.player.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 20) {
      const normX = dx / dist;
      const normY = dy / dist;
      vel.x = normX * speed;
      vel.y = normY * speed;
    }

    if (vel.x !== 0 && vel.y !== 0) {
      vel.x *= 0.707;
      vel.y *= 0.707;
    }

    this.player.setVelocity(vel.x, vel.y);

    // Hard clamp to arena
    const r = 16;
    this.player.x = Phaser.Math.Clamp(
      this.player.x,
      this.ARENA_OFFSET_X + r,
      this.ARENA_OFFSET_X + this.ARENA_WIDTH - r,
    );
    this.player.y = Phaser.Math.Clamp(
      this.player.y,
      this.ARENA_OFFSET_Y + r,
      this.ARENA_OFFSET_Y + this.ARENA_HEIGHT - r,
    );
  }

  private updateBotDecision() {
    // Get all enemies - use for loop instead of forEach to avoid TypeScript narrowing issues
    const enemies = this.enemies.getChildren();
    let nearestEnemy: Phaser.Physics.Arcade.Sprite | null = null;
    let nearestDist = Infinity;

    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i] as Phaser.Physics.Arcade.Sprite;
      const d = Phaser.Math.Distance.Between(this.player.x, this.player.y, e.x, e.y);
      if (d < nearestDist) {
        nearestDist = d;
        nearestEnemy = e;
      }
    }

    // Bot behavior: 
    // 1. If enemies are close, try to kite away from them
    // 2. Otherwise move toward center or random position
    
    if (nearestEnemy && nearestDist < 150) {
      // Too close - run away from enemy
      const angle = Phaser.Math.Angle.Between(
        nearestEnemy.x, nearestEnemy.y,
        this.player.x, this.player.y,
      );
      // Add some randomness to direction (~15% miss rate affects decision too)
      const randomAngle = angle + (Math.random() < BOT_MISS_RATE ? (Math.random() - 0.5) * 1.2 : 0);
      const targetDist = 200 + Math.random() * 150;
      this.botState.targetX = this.player.x + Math.cos(randomAngle) * targetDist;
      this.botState.targetY = this.player.y + Math.sin(randomAngle) * targetDist;
    } else {
      // Safe - move to a random position or center
      // Occasionally change position
      if (Math.random() < 0.3) {
        this.botState.targetX = Phaser.Math.Between(
          this.ARENA_OFFSET_X + 100,
          this.ARENA_OFFSET_X + this.ARENA_WIDTH - 100,
        );
        this.botState.targetY = Phaser.Math.Between(
          this.ARENA_OFFSET_Y + 100,
          this.ARENA_OFFSET_Y + this.ARENA_HEIGHT - 100,
        );
      }
    }

    // Clamp target to arena
    this.botState.targetX = Phaser.Math.Clamp(
      this.botState.targetX,
      this.ARENA_OFFSET_X + 50,
      this.ARENA_OFFSET_X + this.ARENA_WIDTH - 50,
    );
    this.botState.targetY = Phaser.Math.Clamp(
      this.botState.targetY,
      this.ARENA_OFFSET_Y + 50,
      this.ARENA_OFFSET_Y + this.ARENA_HEIGHT - 50,
    );
  }

  private autoFire(time: number, p: Record<string, number>) {
    if (time - p['lastFire'] < p['fireRate']) return;

    // Bot has ~15% miss rate - aim slightly off from nearest enemy
    const target = this.findNearestEnemy();
    if (target) {
      // Apply miss rate - if miss, aim at a random offset position
      let aimX = target.x;
      let aimY = target.y;
      
      if (Math.random() < BOT_MISS_RATE) {
        const offset = 80 + Math.random() * 60;
        const angle = Math.random() * Math.PI * 2;
        aimX = target.x + Math.cos(angle) * offset;
        aimY = target.y + Math.sin(angle) * offset;
      }
      
      this.fireProjectiles(aimX, aimY, p);
      p['lastFire'] = time;
    }
  }

  private findNearestEnemy(): Phaser.Physics.Arcade.Sprite | null {
    let nearest: Phaser.Physics.Arcade.Sprite | null = null;
    let minDist = Infinity;

    this.enemies.getChildren().forEach((child) => {
      const e = child as Phaser.Physics.Arcade.Sprite;
      const d = Phaser.Math.Distance.Between(this.player.x, this.player.y, e.x, e.y);
      if (d < minDist) {
        minDist = d;
        nearest = e;
      }
    });

    return nearest;
  }

  private fireProjectiles(aimX: number, aimY: number, p: Record<string, number>) {
    const count = p['projectileCount'] || 1;
    const speed = p['projectileSpeed'] || 500;
    const baseAngle = Phaser.Math.Angle.Between(
      this.player.x, this.player.y, aimX, aimY,
    );
    const spread = 0.18;

    for (let i = 0; i < count; i++) {
      const angle = baseAngle + (i - (count - 1) / 2) * spread;
      const proj = this.projectiles.create(
        this.player.x, this.player.y, 'projectile',
      ) as Phaser.Physics.Arcade.Sprite;
      proj.setVelocity(Math.cos(angle) * speed, Math.sin(angle) * speed);
      proj.setDepth(5);
      const pr = proj as unknown as Record<string, number>;
      pr['damage'] = p['damage'];
      pr['lifetime'] = 2000;
      pr['bornTime'] = Date.now();
    }
  }

  private updateProjectiles() {
    const now = Date.now();
    this.projectiles.getChildren().forEach((child) => {
      const proj = child as Phaser.Physics.Arcade.Sprite;
      const pr = proj as unknown as Record<string, number>;
      if (now - pr['bornTime'] > pr['lifetime']) {
        proj.destroy();
        return;
      }
      if (
        proj.x < this.ARENA_OFFSET_X - 60 ||
        proj.x > this.ARENA_OFFSET_X + this.ARENA_WIDTH + 60 ||
        proj.y < this.ARENA_OFFSET_Y - 60 ||
        proj.y > this.ARENA_OFFSET_Y + this.ARENA_HEIGHT + 60
      ) {
        proj.destroy();
      }
    });
  }

  private updateEnemies() {
    this.enemies.getChildren().forEach((child) => {
      const e = child as Phaser.Physics.Arcade.Sprite;
      const er = e as unknown as Record<string, number>;
      const angle = Phaser.Math.Angle.Between(e.x, e.y, this.player.x, this.player.y);
      const spd = er['speed'] || 80;
      e.setVelocity(Math.cos(angle) * spd, Math.sin(angle) * spd);
    });
  }

  private projectileHitEnemy(
    projectileObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    enemyObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ) {
    const proj = projectileObj as unknown as Record<string, number>;
    const er = enemyObj as unknown as Record<string, number>;

    er['hp'] = (er['hp'] || 50) - proj['damage'];
    (projectileObj as Phaser.GameObjects.GameObject).destroy();

    if (er['hp'] <= 0) {
      const xpValue = er['xpValue'] || 10;
      const ex = er['x'] as unknown as number;
      const ey = er['y'] as unknown as number;
      (enemyObj as Phaser.GameObjects.GameObject).destroy();
      this.killCount++;
      this.dropXPOrb(ex, ey, xpValue);

      const p = this.player as unknown as Record<string, number>;
      if (p['lifesteal'] > 0) {
        p['hp'] = Math.min(p['hp'] + p['lifesteal'], p['maxHp']);
      }
    }
  }

  private playerHitEnemy(
    _playerObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    enemyObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ) {
    const er = enemyObj as unknown as Record<string, number>;
    const damage = er['damage'] || 10;
    const p = this.player as unknown as Record<string, number>;
    p['hp'] -= damage;

    // Knockback enemy away from player
    const angle = Phaser.Math.Angle.Between(
      this.player.x, this.player.y,
      er['x'] as unknown as number,
      er['y'] as unknown as number,
    );
    (enemyObj as Phaser.Physics.Arcade.Sprite).setVelocity(
      Math.cos(angle) * 320,
      Math.sin(angle) * 320,
    );
  }

  private dropXPOrb(x: number, y: number, xpValue: number) {
    const orb = this.xpOrbs.create(x, y, 'xp_orb') as Phaser.Physics.Arcade.Sprite;
    orb.setDepth(3);
    const or = orb as unknown as Record<string, number>;
    or['xpValue'] = xpValue;
    orb.setVelocity(Phaser.Math.Between(-55, 55), Phaser.Math.Between(-55, 55));
    orb.setDrag(160);
  }

  private updateXPOrbs() {
    const p = this.player as unknown as Record<string, number>;
    const pickR = p['pickupRadius'] || 80;
    const attractR = pickR * 2.2;

    this.xpOrbs.getChildren().forEach((child) => {
      const orb = child as Phaser.Physics.Arcade.Sprite;
      const dist = Phaser.Math.Distance.Between(
        this.player.x, this.player.y, orb.x, orb.y,
      );

      if (dist < pickR) {
        this.collectOrb(orb);
      } else if (dist < attractR) {
        const angle = Phaser.Math.Angle.Between(orb.x, orb.y, this.player.x, this.player.y);
        const spd = 70 + 150 * (pickR / dist);
        orb.setVelocity(Math.cos(angle) * spd, Math.sin(angle) * spd);
      }
    });
  }

  private collectOrb(orb: Phaser.Physics.Arcade.Sprite) {
    const or = orb as unknown as Record<string, number>;
    const xpVal = or['xpValue'] || 10;
    orb.destroy();

    const p = this.player as unknown as Record<string, number>;
    p['xp'] += xpVal;

    const pop = this.add.text(this.player.x, this.player.y - 22, `+${xpVal}`, {
      fontSize: '13px',
      color: '#00ff88',
    }).setDepth(50);
    this.tweens.add({
      targets: pop,
      y: pop.y - 28,
      alpha: 0,
      duration: 700,
      onComplete: () => pop.destroy(),
    });
  }

  private spawnWave() {
    const minutes = this.survivalTime / 60;
    const tankP = Math.min(0.12 + minutes * 0.04, 0.25);
    const swarmerP = Math.min(0.15 + minutes * 0.05, 0.32);
    const roll = Math.random();

    if (roll < swarmerP) {
      this.spawnSwarmerCluster();
    } else if (roll < swarmerP + tankP) {
      this.spawnSingleEnemy('tank');
    } else {
      this.spawnSingleEnemy('melee');
    }
  }

  private edgeSpawnPos(): { x: number; y: number } {
    const side = Phaser.Math.Between(0, 3);
    switch (side) {
      case 0:
        return {
          x: this.ARENA_OFFSET_X + Phaser.Math.Between(0, this.ARENA_WIDTH),
          y: this.ARENA_OFFSET_Y,
        };
      case 1:
        return {
          x: this.ARENA_OFFSET_X + Phaser.Math.Between(0, this.ARENA_WIDTH),
          y: this.ARENA_OFFSET_Y + this.ARENA_HEIGHT,
        };
      case 2:
        return {
          x: this.ARENA_OFFSET_X,
          y: this.ARENA_OFFSET_Y + Phaser.Math.Between(0, this.ARENA_HEIGHT),
        };
      default:
        return {
          x: this.ARENA_OFFSET_X + this.ARENA_WIDTH,
          y: this.ARENA_OFFSET_Y + Phaser.Math.Between(0, this.ARENA_HEIGHT),
        };
    }
  }

  private spawnSingleEnemy(typeName: 'melee' | 'tank') {
    const cfg = ENEMY_TYPES[typeName];
    const pos = this.edgeSpawnPos();
    const e = this.enemies.create(pos.x, pos.y, `enemy_${typeName}`) as Phaser.Physics.Arcade.Sprite;
    e.setCircle(cfg.size);
    e.setDepth(7);
    const er = e as unknown as Record<string, number | string>;
    er['hp'] = cfg.hp;
    er['maxHp'] = cfg.hp;
    er['speed'] = cfg.speed * (1 + (this.survivalTime / 120) * 0.15);
    er['enemyType'] = typeName;
    er['xpValue'] = cfg.xpValue;
    er['damage'] = cfg.damage;
  }

  private spawnSwarmerCluster() {
    const count = Phaser.Math.Between(5, 8);
    const pos = this.edgeSpawnPos();
    const cfg = ENEMY_TYPES['swarmer'];

    for (let i = 0; i < count; i++) {
      const ox = Phaser.Math.Between(-38, 38);
      const oy = Phaser.Math.Between(-38, 38);
      const e = this.enemies.create(pos.x + ox, pos.y + oy, 'enemy_swarmer') as Phaser.Physics.Arcade.Sprite;
      e.setCircle(cfg.size);
      e.setDepth(7);
      const er = e as unknown as Record<string, number | string>;
      er['hp'] = cfg.hp;
      er['maxHp'] = cfg.hp;
      er['speed'] = cfg.speed;
      er['enemyType'] = 'swarmer';
      er['xpValue'] = cfg.xpValue;
      er['damage'] = cfg.damage;
    }
  }

  private triggerGameOver() {
    this.isGameOver = true;
    this.endAttractMode();
  }

  private endAttractMode() {
    if (this.isGameOver) {
      // Cancel the timer if game ended naturally
      this.attractTimerEvent?.remove();
    }
    
    // Emit event to TitleScene
    this.events.emit('attractModeEnd');
    
    // Stop this scene
    this.scene.stop();
  }

  // Public API for testing
  public getGameState() {
    const p = this.player as unknown as Record<string, number>;
    return {
      player: {
        x: this.player.x,
        y: this.player.y,
        hp: p['hp'],
        maxHp: p['maxHp'],
        speed: p['speed'],
        damage: p['damage'],
        fireRate: p['fireRate'],
        projectileCount: p['projectileCount'],
        projectileSpeed: p['projectileSpeed'],
        pickupRadius: p['pickupRadius'],
        hpRegen: p['hpRegen'],
        lifesteal: p['lifesteal'],
        level: p['level'],
        xp: p['xp'],
        xpToNextLevel: p['xpToNextLevel'],
      },
      killCount: this.killCount,
      survivalTime: this.survivalTime,
      enemyCount: this.enemies.getChildren().length,
      difficultyMultiplier: this.difficultyMultiplier,
      arenaBounds: {
        minX: this.ARENA_OFFSET_X,
        maxX: this.ARENA_OFFSET_X + this.ARENA_WIDTH,
        minY: this.ARENA_OFFSET_Y,
        maxY: this.ARENA_OFFSET_Y + this.ARENA_HEIGHT,
      },
      enemies: this.enemies.getChildren().map((child) => {
        const e = child as Phaser.Physics.Arcade.Sprite;
        const er = e as unknown as Record<string, number | string>;
        return {
          type: er['enemyType'],
          hp: er['hp'],
          maxHp: er['maxHp'],
          speed: er['speed'],
          x: e.x,
          y: e.y,
        };
      }),
      enemyTypeConfig: {
        melee: { speed: ENEMY_TYPES['melee'].speed, hp: ENEMY_TYPES['melee'].hp },
        tank: { speed: ENEMY_TYPES['tank'].speed, hp: ENEMY_TYPES['tank'].hp },
        swarmer: { speed: ENEMY_TYPES['swarmer'].speed, hp: ENEMY_TYPES['swarmer'].hp },
      },
      isAttractMode: true,
    };
  }

  public setPlayerHP(hp: number) {
    (this.player as unknown as Record<string, number>)['hp'] = hp;
  }
}
